import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import {
  useAclModuleList,
  useAclSettingDetail,
  useUpdateAclSetting,
} from '../api';
import { AclPermissionMatrix } from '../components/AclPermissionMatrix';
import {
  buildModuleOperation,
  hydratePermissions,
  withParsedChildren,
  type PermissionFlags,
  type PermissionsMap,
} from '../helpers';

export function UpdateAclSettingPage() {
  const navigate = useNavigate();
  const userId = Number(useAuthStore((s) => s.userId));
  const { id } = useParams<{ id: string }>();
  const settingId = Number(id);

  const moduleList = useAclModuleList();
  const detail = useAclSettingDetail(settingId);
  const update = useUpdateAclSetting();

  const modules = useMemo(
    () => withParsedChildren(moduleList.data ?? []),
    [moduleList.data],
  );

  const [aclName, setAclName] = useState('');
  const [permissions, setPermissions] = useState<PermissionsMap>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [seeded, setSeeded] = useState(false);

  // Hydrate name + permissions once BOTH module list and setting detail
  // have resolved. We only seed once (subsequent module-list refetches
  // shouldn't blow away unsaved edits).
  useEffect(() => {
    if (seeded) return;
    if (!modules.length) return;
    if (!detail.data) return;
    setAclName(detail.data.aclName ?? '');
    const { permissions: perms, expanded: exp } = hydratePermissions(
      modules,
      detail.data.moduleOperation,
    );
    setPermissions(perms);
    setExpanded(exp);
    setSeeded(true);
  }, [modules, detail.data, seeded]);

  const handleToggle = (
    rowId: string,
    key: keyof PermissionFlags,
    next: boolean,
  ) => {
    setPermissions((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] ?? {}), [key]: next },
    }));
  };

  const handleToggleAll = (
    rowId: string,
    keys: readonly (keyof PermissionFlags)[],
    next: boolean,
  ) => {
    setPermissions((prev) => {
      const cur = { ...(prev[rowId] ?? {}) };
      for (const k of keys) cur[k] = next;
      return { ...prev, [rowId]: cur };
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!aclName.trim()) {
      toast.error('Role name is required.');
      return;
    }
    if (!Number.isFinite(settingId)) {
      toast.error('Invalid role id.');
      return;
    }
    update.mutate(
      {
        id: settingId,
        aclName: aclName.trim(),
        userId,
        moduleOperation: buildModuleOperation(modules, permissions),
        // Preserve any moduleListingData the legacy app stored without
        // touching it — richer multi-select scoping is a v2.
        moduleListingData: detail.data?.moduleListingData ?? '{}',
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Role updated');
          navigate('/acl-settings');
        },
        onError: () => toast.error('Could not update the role.'),
      },
    );
  };

  const initialLoading = detail.isLoading || moduleList.isLoading || !seeded;

  if (initialLoading) {
    return (
      <ScrollPage>
        <PageHeader title="Edit Role" description="Loading…" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-9 w-full max-w-md" />
            <Skeleton className="mt-4 h-40 w-full" />
          </CardContent>
        </Card>
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Edit Role"
        description="Adjust permissions for this role. Changes apply on the next login."
        actions={
          <Button asChild variant="outline">
            <Link to="/acl-settings">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="grid gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Role details</CardTitle>
            <CardDescription>
              Rename or leave as-is. Users will see this in the role dropdown.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-1.5 sm:max-w-md">
              <Label htmlFor="aclName">
                Role name<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Input
                id="aclName"
                value={aclName}
                onChange={(e) => setAclName(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Permissions</CardTitle>
            <CardDescription>
              Toggle which actions this role can perform per module.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AclPermissionMatrix
              modules={modules}
              permissions={permissions}
              expanded={expanded}
              onToggle={handleToggle}
              onToggleAll={handleToggleAll}
              onExpand={(rowId, next) =>
                setExpanded((p) => ({ ...p, [rowId]: next }))
              }
            />
          </CardContent>
        </Card>

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={update.isPending}>
            {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </Button>
          <Button asChild type="button" variant="ghost">
            <Link to="/acl-settings">Cancel</Link>
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
}
